import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { LogOut, Menu, X } from 'lucide-react';
import { useAuth, useUser } from '../../auth/AuthContext';
import { ROLE_LABEL, areaFor } from '../../auth/roles';
import { useApi } from '../../hooks/useApi';
import { NAV } from './nav';

interface PendingCount {
  metrics: { pendingTitles: number };
}

export function AppShell() {
  const user = useUser();
  const { logout } = useAuth();
  const area = areaFor(user.role);
  const nav = NAV[area];
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Only reviewers see the number of submissions waiting; it is a live count, not decoration.
  const canSeeQueue = area === 'staff' && user.role !== 'support';
  const queue = useApi<PendingCount>(canSeeQueue ? '/admin/metrics' : null);
  const pending = queue.data?.metrics.pendingTitles ?? 0;

  const sidebar = (
    <div className="flex h-full flex-col bg-[#0f0a17] border-r border-[#241b33]">
      <div className="px-5 py-5 border-b border-[#241b33]">
        <div className="text-base font-bold tracking-tight text-white">Drameeka</div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-violet-400 mt-0.5">
          {nav.title}
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto p-3 space-y-1" aria-label="Main">
        {nav.items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-violet-600/15 text-white border border-violet-500/30'
                  : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
              }`
            }
          >
            <item.icon className="w-4 h-4 shrink-0" />
            <span className="flex-1">{item.label}</span>
            {item.to === '/staff/review' && pending > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-[10px] font-bold">
                {pending}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-[#241b33]">
        <div className="px-3 py-2">
          <div className="text-sm font-semibold text-white truncate">
            {user.displayName || user.email}
          </div>
          <div className="text-[11px] text-gray-400 truncate">{user.email}</div>
          <div className="text-[11px] text-violet-300 mt-0.5">{ROLE_LABEL[user.role]}</div>
        </div>
        <button
          onClick={() => void logout()}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-full bg-[#090814] text-[#f4f1fa]">
      <aside className="hidden md:block w-60 shrink-0">{sidebar}</aside>

      {open && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64">{sidebar}</div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <div className="md:hidden flex items-center gap-3 px-4 h-14 border-b border-[#241b33] bg-[#0f0a17]">
          <button
            onClick={() => setOpen((o) => !o)}
            className="p-2 -ml-2 text-gray-300"
            aria-label={open ? 'Close menu' : 'Open menu'}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <span className="font-bold text-white">Drameeka</span>
        </div>
        <main key={location.pathname} className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
